/**
 * Replay Viewer Component
 * Displays game replays with playback controls
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Howl } from 'howler';
import BackgroundMusic from '../mfersbeheaded/BackgroundMusic';
import GameBoard from '../mfersbeheaded/GameBoard';
import GameHUD from '../mfersbeheaded/GameHUD';
import LEVELS from '../mfersbeheaded/levels';
import TEST_LEVELS from '../mfersbeheaded/test_levels';
import { useGameAudio } from '../mfersbeheaded/hooks/useGameAudio';
import '../mfersbeheaded/Board.css'; // Reuse game styling
import './ReplayViewer.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function ReplayViewer({ twitterUser }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef();

  // Replay data
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Playback state
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [targetTime, setTargetTime] = useState(0); // Target time for smooth interpolation

  // Board state
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [powerUps, setPowerUps] = useState(0);
  const [highlightedSwap, setHighlightedSwap] = useState(null); // { from: [row, col], to: [row, col] }
  const [previewSwap, setPreviewSwap] = useState(null); // Preview of next swap { from: [row, col], to: [row, col], direction: 'up'|'down'|'left'|'right' }
  const [musicMuted, setMusicMuted] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);

  // Keyboard toggle for coordinate overlay (secret debug feature)
  useEffect(() => {
    const handleKeyPress = (event) => {
      console.log('[ReplayViewer] Key pressed:', event.key);
      if (event.key === 'c' || event.key === 'C') {
        console.log('[ReplayViewer] Toggling showCoordinates');

        // Dump board state to console
        console.log('=== REPLAY BOARD STATE DUMP ===');
        console.log('Session ID:', sessionId);
        console.log('Board size:', board.length, 'x', board[0]?.length);
        console.log('Board state:');
        board.forEach((row, rowIndex) => {
          console.log(`Row ${rowIndex}:`, row.join(', '));
        });
        console.log('Score:', score);
        console.log('Moves:', moves);
        console.log('Current time:', currentTime, 'ms');
        console.log('Current event index:', currentEventIndex);
        console.log('Total events:', events.length);
        console.log('===============================');

        setShowCoordinates(prev => {
          console.log('[ReplayViewer] New showCoordinates value:', !prev);
          return !prev;
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [board, score, moves, currentTime, currentEventIndex, events.length, sessionId]);

  // Check if we're in development/local mode
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Export session as JSON fixture
  const handleExportSession = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/replay/sessions/${sessionId}/export`);
      if (!response.ok) {
        throw new Error('Failed to export session');
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `replay_${sessionId}_${session?.level_id || 'unknown'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log('✅ Replay exported successfully!');
    } catch (error) {
      console.error('Error exporting session:', error);
      alert('Failed to export session. See console for details.');
    }
  };

  // Initialize audio - will be null until session loads
  const config = session?.config || {};
  const { playSound, playWrongSound, playSuperPowerUpSound } = useGameAudio(config, musicMuted);

  // Function to play random advanced sound (for 4+ matches)
  const playAdvancedSound = () => {
    if (musicMuted || !config.ADV_SOUND_CLIPS || config.ADV_SOUND_CLIPS.length === 0) {
      return;
    }
    const randomIndex = Math.floor(Math.random() * config.ADV_SOUND_CLIPS.length);
    const sound = new Howl({
      src: [config.ADV_SOUND_CLIPS[randomIndex]],
      autoplay: true,
      loop: false,
      volume: 0.5,
    });
  };

  // Refs
  const playbackTimerRef = useRef(null);
  const playbackStartTimeRef = useRef(0); // When playback started (real time)
  const playbackStartGameTimeRef = useRef(0); // Game time when playback started
  const pendingEventIndexRef = useRef(-1); // Track which event we're waiting for

  // Load replay session
  useEffect(() => {
    loadReplay();
  }, [sessionId]);

  // Apply body class for scrolling and background styling
  useEffect(() => {
    document.body.classList.add('replay-page');

    // Apply background image if available
    if (session?.config?.BG_IMAGE) {
      document.body.style.backgroundImage = `url(${session.config.BG_IMAGE})`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundAttachment = 'fixed';
    }

    return () => {
      document.body.classList.remove('replay-page');
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundAttachment = '';
    };
  }, [session]);

  // Smooth timer interpolation while playing - synced to real elapsed time
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    const intervalMs = 50; // Update timer every 50ms for smooth visual

    const timerInterval = setInterval(() => {
      const now = Date.now();
      const realElapsed = now - playbackStartTimeRef.current;
      const gameTimeElapsed = realElapsed; // 1x speed
      const newGameTime = playbackStartGameTimeRef.current + gameTimeElapsed;

      setCurrentTime(prevTime => {
        // Don't go beyond the next event's time
        if (targetTime > 0 && newGameTime >= targetTime) {
          return targetTime;
        }
        return newGameTime;
      });
    }, intervalMs);

    return () => clearInterval(timerInterval);
  }, [isPlaying, targetTime]);

  const loadReplay = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${SERVER_URL}/api/replay/sessions/${sessionId}`);

      if (!response.ok) {
        throw new Error('Replay not found');
      }

      const data = await response.json();

      // Look up level config from levels.js or test_levels.js based on level_id
      let levelConfig = Object.values(LEVELS).find(level => level.ID === data.session.level_id);

      // If not found in regular levels, check test levels
      if (!levelConfig) {
        levelConfig = Object.values(TEST_LEVELS).find(level => level.ID === data.session.level_id);
      }

      if (!levelConfig) {
        throw new Error(`Level config not found for level_id: ${data.session.level_id}`);
      }

      // Use level config from levels.js or test_levels.js instead of stored game_config
      const normalizedSession = {
        ...data.session,
        config: levelConfig
      };
      setSession(normalizedSession);

      // Filter out BOARD_STATE events (keep only semantic events: CLEAR, GRAVITY, FILL)
      // Keep BOARD_INIT (first event), but remove all other BOARD_STATE events
      const filteredEvents = data.events.filter((event, index) => {
        // Keep first event (BOARD_INIT)
        if (index === 0) return true;
        // Filter out BOARD_STATE events
        if (event.event_type === 'BOARD_STATE') return false;
        // Keep all other events
        return true;
      });

      setEvents(filteredEvents);

      // Seek to the end to show final score
      if (filteredEvents.length > 0) {
        const lastEvent = filteredEvents[filteredEvents.length - 1];
        const maxTime = lastEvent.timestamp_ms;

        // Initialize board and seek to end
        const initEvent = filteredEvents[0];
        if (initEvent.event_type === 'BOARD_INIT') {
          const boardSize = initEvent.data.board_size;
          const initialBoard = createEmptyBoard(boardSize);
          applyStateChanges(initialBoard, initEvent.data.changes);

          let finalBoard = initialBoard.map(row => [...row]);
          let finalScore = 0;
          let finalMoves = 0;
          let finalPowerUps = 0;

          // Apply all events to get final state
          filteredEvents.forEach((event, index) => {
            if (index === 0) return; // Skip init event

            switch (event.event_type) {
              // Skip BOARD_STATE - use semantic events (CLEAR, GRAVITY, FILL) instead
              // case 'BOARD_STATE':
              case 'BARREL_PLACE':
                applyStateChanges(finalBoard, event.data.changes);
                break;
              case 'CLEAR':
                // Clear events contain cells that were cleared
                if (event.data.cleared) {
                  applyStateChanges(finalBoard, event.data.cleared);
                }
                break;
              case 'GRAVITY':
                // Gravity events contain movements - apply the destination states
                if (event.data.movements) {
                  event.data.movements.forEach(movement => {
                    const [toRow, toCol] = movement.to;
                    finalBoard[toRow][toCol] = movement.value;
                  });
                  // Note: Source cells becoming dead should be handled by CLEAR events
                }
                break;
              case 'FILL':
                // Fill events contain new pieces added
                if (event.data.new_pieces) {
                  applyStateChanges(finalBoard, event.data.new_pieces);
                }
                break;
              case 'SCORE_UPDATE':
                finalScore = event.data.score;
                break;
              case 'GAME_STATE':
                if (event.data.moves_used !== undefined) {
                  finalMoves = event.data.moves_used;
                }
                break;
              case 'POWER_UP':
                finalPowerUps++;
                break;
              default:
                break;
            }
          });

          setBoard(finalBoard);
          setScore(finalScore);
          setMoves(finalMoves);
          setPowerUps(finalPowerUps);
          setCurrentTime(maxTime);
          setCurrentEventIndex(data.events.length - 1);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading replay:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Create empty board
  const createEmptyBoard = (size) => {
    return Array(size)
      .fill(null)
      .map(() => Array(size).fill(0));
  };

  // Apply state changes to board
  const applyStateChanges = (boardState, changes) => {
    changes.forEach(([row, col, value]) => {
      if (boardState[row] && boardState[row][col] !== undefined) {
        boardState[row][col] = value;
      }
    });
  };

  // Calculate swap direction
  const calculateSwapDirection = (from, to) => {
    const [fromRow, fromCol] = from;
    const [toRow, toCol] = to;

    if (toRow < fromRow) return 'up';
    if (toRow > fromRow) return 'down';
    if (toCol < fromCol) return 'left';
    if (toCol > fromCol) return 'right';
    return null;
  };

  // Peek ahead to find next swap event (only if it's the immediate next event)
  const peekNextSwap = (fromIndex) => {
    // Only show preview if the NEXT event is a SWAP
    // Don't show if there are FILL, GRAVITY, etc. events before the next swap
    const nextEventIndex = fromIndex + 1;
    if (nextEventIndex < events.length) {
      const nextEvent = events[nextEventIndex];
      if (nextEvent.event_type === 'SWAP') {
        const direction = calculateSwapDirection(nextEvent.data.from, nextEvent.data.to);
        return {
          from: nextEvent.data.from,
          to: nextEvent.data.to,
          direction: direction,
          valid: nextEvent.data.valid
        };
      }
    }
    return null; // Next event is not a swap, or no more events
  };

  // Playback controls
  const play = () => {
    // If at the end, restart from beginning
    if (currentEventIndex >= events.length - 1) {
      restart();
      // Start playing after restart
      setTimeout(() => {
        setIsPlaying(true);
        if (events.length > 0) {
          setTargetTime(events[0].timestamp_ms);
        }
        playbackStartTimeRef.current = Date.now();
        playbackStartGameTimeRef.current = 0;
      }, 0);
      return;
    }

    setIsPlaying(true);
    // Set initial target if starting from beginning
    if (currentEventIndex === 0 && events.length > 0) {
      setTargetTime(events[0].timestamp_ms);
    }
    // Record when playback started (for synced timer)
    playbackStartTimeRef.current = Date.now();
    playbackStartGameTimeRef.current = currentTime;
  };

  const pause = () => {
    setIsPlaying(false);
    if (playbackTimerRef.current) {
      clearTimeout(playbackTimerRef.current);
    }
    pendingEventIndexRef.current = -1; // Clear pending event
  };

  const restart = () => {
    setCurrentEventIndex(0);
    setCurrentTime(0);
    setTargetTime(0);
    setScore(0);
    setMoves(0);
    setPowerUps(0);
    setHighlightedSwap(null);

    // Reset board to initial state
    if (events.length > 0 && events[0].event_type === 'BOARD_INIT') {
      console.log('=== BOARD_INIT DEBUG (reset) ===');
      console.log('BOARD_INIT event data:', events[0].data);
      console.log('Board size:', events[0].data.board_size);
      console.log('Number of changes:', events[0].data.changes.length);
      console.log('First 10 changes:', events[0].data.changes.slice(0, 10));

      const boardSize = events[0].data.board_size;
      const initialBoard = createEmptyBoard(boardSize);
      console.log('Empty board created:', initialBoard);

      applyStateChanges(initialBoard, events[0].data.changes);
      console.log('Board after applying changes:');
      initialBoard.forEach((row, i) => {
        console.log(`Row ${i}:`, row.join(', '));
      });
      console.log('================================');

      setBoard(initialBoard);
    }

    setIsPlaying(false);
    pendingEventIndexRef.current = -1; // Clear pending event
  };

  const seek = (seekTime) => {
    // Find all events up to target time
    const eventsUpToTime = events.filter(e => e.timestamp_ms <= seekTime);

    // Rebuild state from scratch
    if (events.length > 0 && events[0].event_type === 'BOARD_INIT') {
      console.log('=== BOARD_INIT DEBUG (seek) ===');
      console.log('BOARD_INIT event data:', events[0].data);
      console.log('Board size:', events[0].data.board_size);
      console.log('Number of changes:', events[0].data.changes.length);
      console.log('First 10 changes:', events[0].data.changes.slice(0, 10));

      const boardSize = events[0].data.board_size;
      const newBoard = createEmptyBoard(boardSize);
      console.log('Empty board created:', newBoard);

      applyStateChanges(newBoard, events[0].data.changes);
      console.log('Board after applying changes:');
      newBoard.forEach((row, i) => {
        console.log(`Row ${i}:`, row.join(', '));
      });
      console.log('================================');

      let newScore = 0;
      let newMoves = 0;
      let newPowerUps = 0;
      let lastSwap = null;

      // Apply all events up to target time
      eventsUpToTime.forEach((event, index) => {
        if (index === 0) return; // Skip init event

        switch (event.event_type) {
          // Skip BOARD_STATE - use semantic events (CLEAR, GRAVITY, FILL) instead
          // case 'BOARD_STATE':
          case 'BARREL_PLACE':
            applyStateChanges(newBoard, event.data.changes);
            break;
          case 'CLEAR':
            if (event.data.cleared) {
              applyStateChanges(newBoard, event.data.cleared);
            }
            break;
          case 'GRAVITY':
            if (event.data.movements) {
              event.data.movements.forEach(movement => {
                const [toRow, toCol] = movement.to;
                newBoard[toRow][toCol] = movement.value;
              });
            }
            break;
          case 'FILL':
            if (event.data.new_pieces) {
              applyStateChanges(newBoard, event.data.new_pieces);
            }
            break;
          case 'SWAP':
            // Apply valid swaps to board
            if (event.data.valid) {
              const [fromRow, fromCol] = event.data.from;
              const [toRow, toCol] = event.data.to;
              const temp = newBoard[fromRow][fromCol];
              newBoard[fromRow][fromCol] = newBoard[toRow][toCol];
              newBoard[toRow][toCol] = temp;
            }
            lastSwap = { from: event.data.from, to: event.data.to };
            break;
          case 'SCORE_UPDATE':
            newScore = event.data.score;
            break;
          case 'GAME_STATE':
            if (event.data.moves_used !== undefined) {
              newMoves = event.data.moves_used;
            }
            break;
          case 'POWER_UP':
            newPowerUps++;
            break;
          default:
            break;
        }
      });

      setHighlightedSwap(lastSwap);

      setBoard(newBoard);
      setScore(newScore);
      setMoves(newMoves);
      setPowerUps(newPowerUps);
      setCurrentTime(seekTime);
      setCurrentEventIndex(Math.max(0, eventsUpToTime.length - 1));

      // Update target time for next event
      const nextEventIndex = eventsUpToTime.length;
      if (nextEventIndex < events.length) {
        setTargetTime(events[nextEventIndex].timestamp_ms);
      }

      // Reset playback timing for smooth timer
      playbackStartTimeRef.current = Date.now();
      playbackStartGameTimeRef.current = seekTime;

      // Clear pending event
      pendingEventIndexRef.current = -1;
    }
  };


  // Update preview swap whenever current event changes
  useEffect(() => {
    if (events.length > 0) {
      const nextSwap = peekNextSwap(currentEventIndex);
      setPreviewSwap(nextSwap);
    }
  }, [currentEventIndex, events]);

  // Playback loop
  useEffect(() => {
    if (!isPlaying || currentEventIndex >= events.length - 1) {
      if (isPlaying && currentEventIndex >= events.length - 1) {
        setIsPlaying(false); // Auto-stop at end
      }
      return;
    }

    const nextEvent = events[currentEventIndex + 1];
    const currentEvent = events[currentEventIndex];

    if (!nextEvent || !currentEvent) {
      console.warn('Missing event data', { currentEventIndex, eventsLength: events.length });
      return;
    }

    const waitingForIndex = currentEventIndex + 1;

    // Skip if we're already waiting for this event
    if (pendingEventIndexRef.current === waitingForIndex) {
      return;
    }

    // Set target time for smooth timer interpolation
    setTargetTime(nextEvent.timestamp_ms);

    // Calculate time until next event
    const timeDelta = nextEvent.timestamp_ms - currentEvent.timestamp_ms;
    const adjustedDelay = Math.max(10, timeDelta); // Min 10ms

    // Mark that we're waiting for this event
    pendingEventIndexRef.current = waitingForIndex;

    playbackTimerRef.current = setTimeout(() => {
      // Only apply if we're still waiting for this event
      if (waitingForIndex !== currentEventIndex + 1) {
        return;
      }

      const newIndex = waitingForIndex;
      const event = events[newIndex];

      // Apply event
      switch (event.event_type) {
        // Skip BOARD_STATE - use semantic events (CLEAR, GRAVITY, FILL) instead
        // case 'BOARD_STATE':
        //   setBoard(prevBoard => {
        //     const newBoard = prevBoard.map(row => [...row]);
        //     applyStateChanges(newBoard, event.data.changes);
        //     return newBoard;
        //   });
        //
        //   // Play advanced sound for 4+ matches (detected by sweep context with >3 changes)
        //   if (event.event_type === 'BOARD_STATE' && event.data.context === 'sweep' && event.data.changes.length > 3) {
        //     playAdvancedSound();
        //   }
        //   break;
        case 'BARREL_PLACE':
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(row => [...row]);
            applyStateChanges(newBoard, event.data.changes);
            return newBoard;
          });
          break;
        case 'CLEAR':
          // Enhanced: Clear events show pieces being cleared
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(row => [...row]);
            if (event.data.cleared) {
              applyStateChanges(newBoard, event.data.cleared);
            }
            return newBoard;
          });
          // Play sound for matches being cleared
          if (event.data.cleared && event.data.cleared.length > 3) {
            playAdvancedSound();
          } else if (event.data.cleared && event.data.cleared.length > 0) {
            playSound();
          }
          break;
        case 'GRAVITY':
          // Enhanced: Gravity events show pieces falling
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(row => [...row]);
            if (event.data.movements) {
              event.data.movements.forEach(movement => {
                const [toRow, toCol] = movement.to;
                newBoard[toRow][toCol] = movement.value;
              });
            }
            return newBoard;
          });
          break;
        case 'FILL':
          // Enhanced: Fill events show new pieces appearing
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(row => [...row]);
            if (event.data.new_pieces) {
              applyStateChanges(newBoard, event.data.new_pieces);
            }
            return newBoard;
          });
          break;
        case 'SWAP':
          // SWAP events modify the board by swapping two cells (only if valid)
          if (event.data.valid) {
            setBoard(prevBoard => {
              const newBoard = prevBoard.map(row => [...row]);
              const [fromRow, fromCol] = event.data.from;
              const [toRow, toCol] = event.data.to;
              const temp = newBoard[fromRow][fromCol];
              newBoard[fromRow][fromCol] = newBoard[toRow][toCol];
              newBoard[toRow][toCol] = temp;
              return newBoard;
            });
            playSound();
          } else {
            playWrongSound();
          }

          // Highlight the swapped pieces
          setHighlightedSwap({
            from: event.data.from,
            to: event.data.to
          });
          // Clear highlight after 1 second
          setTimeout(() => setHighlightedSwap(null), 1000);
          break;
        case 'SCORE_UPDATE':
          setScore(event.data.score);
          break;
        case 'GAME_STATE':
          if (event.data.moves_used !== undefined) {
            setMoves(event.data.moves_used);
          }
          if (event.data.state === 2) {
            // Game complete
            setIsPlaying(false);
          }
          break;
        case 'POWER_UP':
          setPowerUps(prev => prev + 1);
          playSuperPowerUpSound();
          break;
        default:
          break;
      }

      // Snap to exact event timestamp
      setCurrentTime(event.timestamp_ms);
      setCurrentEventIndex(newIndex);

      // Reset playback timing references for smooth timer sync
      playbackStartTimeRef.current = Date.now();
      playbackStartGameTimeRef.current = event.timestamp_ms;

      // Clear pending event marker
      pendingEventIndexRef.current = -1;
    }, adjustedDelay);

    return () => {
      if (playbackTimerRef.current) {
        clearTimeout(playbackTimerRef.current);
      }
      // Don't clear pendingEventIndexRef here - we still want to prevent re-setup
    };
  }, [isPlaying, currentEventIndex, events]);

  // Loading state
  if (loading) {
    return (
      <div className="replay-viewer loading">
        <h2>Loading replay...</h2>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="replay-viewer error">
        <h2>Error loading replay</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/mfersbeheaded/leaderboard')}>← Leaderboard</button>
      </div>
    );
  }

  // No session
  if (!session) {
    return (
      <div className="replay-viewer error">
        <h2>Replay not found</h2>
        <button onClick={() => navigate('/mfersbeheaded/leaderboard')}>← Leaderboard</button>
      </div>
    );
  }

  const maxTime = events.length > 0 ? events[events.length - 1].timestamp_ms : 0;
  const progressPercent = maxTime > 0 ? (currentTime / maxTime) * 100 : 0;

  // Get event markers for timeline
  const getEventMarkers = () => {
    return events.map((event, index) => ({
      index,
      time: event.timestamp_ms,
      type: event.event_type,
      percent: maxTime > 0 ? (event.timestamp_ms / maxTime) * 100 : 0,
    }));
  };

  // Get event type color
  const getEventColor = (eventType) => {
    const colors = {
      'BOARD_INIT': '#888',
      'BOARD_STATE': '#4a90e2',
      'SWAP': '#f39c12',
      'SCORE_UPDATE': '#27ae60',
      'POWER_UP': '#e74c3c',
      'GAME_STATE': '#9b59b6',
      'BARREL_PLACE': '#95a5a6',
    };
    return colors[eventType] || '#333';
  };

  // Get event type label
  const getEventLabel = (eventType) => {
    const labels = {
      'BOARD_INIT': 'Init',
      'BOARD_STATE': 'Board',
      'SWAP': 'Swap',
      'SCORE_UPDATE': 'Score',
      'POWER_UP': 'Power',
      'GAME_STATE': 'State',
      'BARREL_PLACE': 'Barrel',
    };
    return labels[eventType] || eventType;
  };

  // Jump to specific event
  const jumpToEvent = (eventIndex) => {
    if (eventIndex < 0 || eventIndex >= events.length) return;
    pause();
    const targetTime = events[eventIndex].timestamp_ms;
    seek(targetTime);
  };

  const levelClass = session?.level_id ? `level-${session.level_id}` : '';

  // Dynamic background styles for special levels
  const getLevelContainerStyle = () => {
    if (session?.level_id === 'house_songs') {
      return {
        backgroundImage: 'url(/images/mfersbeheaded/house_songs_title.png_small)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center top'
      };
    }
    return {};
  };

  const getGameContainerStyle = () => {
    if (session?.level_id === 'house_songs') {
      return {
        backgroundImage: 'url(/images/mfersbeheaded/house_songs_background.png_small)',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat'
      };
    }
    return {};
  };

  return (
    <>
    <div className={`gameWiderContainer ${levelClass}`} style={{backgroundImage: session?.config?.BG_IMAGE ? `url(${session.config.BG_IMAGE})` : 'none'}}>
      <div className={`gameContainer ${levelClass}`} style={getGameContainerStyle()}>
        <div className="header">
          <div className={`levelContainer ${levelClass}`} style={getLevelContainerStyle()}>
            {session?.level_id !== 'house_songs' && session?.level_name}
          </div>
          <div className="statsContainer">
            {/* Use shared GameHUD component */}
            <GameHUD
              score={score}
              moves={moves}
              timeRemaining={(maxTime - currentTime) / 1000}
              levelClass={levelClass}
              showTimer={false}
              showMoves={true}
              additionalInfo={
                <div className={`replay-indicator ${levelClass}`}>
                  📺 {session.player_name || session.twitter_user?.screen_name || 'Anonymous'}
                </div>
              }
            />
          </div>
        </div>

        {/* Use shared GameBoard component */}
        <GameBoard
          board={board}
          levelClass={levelClass}
          levelID={session.level_id}
          imageMap={session.config.IMAGE_MAP}
          selectedJewel={
            highlightedSwap ? (
              highlightedSwap.from ? { rowIndex: highlightedSwap.from[0], colIndex: highlightedSwap.from[1] } : null
            ) : null
          }
          shouldBeCleared={[]}
          readOnly={true}
          showCoordinates={showCoordinates}
          previewSwap={previewSwap}
        />

      {/* Timeline - between board and buttons */}
      <div className="replay-timeline">
        <div className="timeline-timer">
          {(currentTime / 1000).toFixed(1)} / {(maxTime / 1000).toFixed(1)} sec
        </div>
        <div className="timeline">
          <input
            type="range"
            min="0"
            max={maxTime}
            value={currentTime}
            onChange={(e) => {
              pause();
              seek(parseInt(e.target.value));
            }}
            className="timeline-slider"
          />
          <div className="timeline-progress" style={{ width: `${progressPercent}%` }} />

          {/* Playhead indicator */}
          <div className="timeline-playhead" style={{ left: `${progressPercent}%` }} />

          {/* Event markers on timeline */}
          <div className="event-markers">
            {getEventMarkers().map((marker, idx) => (
              <div
                key={idx}
                className={`event-marker ${marker.index === currentEventIndex ? 'active' : ''}`}
                style={{
                  left: `${marker.percent}%`,
                  backgroundColor: getEventColor(marker.type),
                }}
                onClick={() => jumpToEvent(marker.index)}
                title={`${getEventLabel(marker.type)} at ${(marker.time / 1000).toFixed(1)}s`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="buttonContainer">
        {isPlaying ? (
          <button className={`play-again ${levelClass}`} onClick={pause}>⏸ Pause</button>
        ) : (
          <button className={`play-again ${levelClass}`} onClick={play}>
            {currentEventIndex >= events.length - 1 ? '▶ Replay' : '▶ Play'}
          </button>
        )}
        <button
          className={`play-again share-x-button ${levelClass}`}
          onClick={() => {
            const replayUrl = `${window.location.origin}/mfersbeheaded/replay/${sessionId}`;
            const finalScore = session.final_score || score;

            // Check if logged-in user is the one who played this replay
            const isOwnReplay = twitterUser && session.twitter_user && (
              twitterUser.user_id === session.twitter_user.user_id ||
              twitterUser.user_id === session.twitter_user.id_str ||
              twitterUser.screen_name === session.twitter_user.screen_name
            );

            // Use first-person if it's their replay, third-person otherwise
            const tweetText = isOwnReplay
              ? `🎮 Just crushed ${session.level_name} on mfers arcade!\n\n🎯 Score: ${finalScore}\n\nWatch the replay and see if you can beat it! 👇`
              : `🎮 Check out this replay of ${session.level_name} on mfers arcade!\n\n🎯 Score: ${finalScore}\n\nThink you can beat it? 👇`;

            const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(replayUrl)}`;
            window.open(twitterIntentUrl, '_blank', 'noopener,noreferrer');
          }}
        >
          <img src="/images/x-logo-black.png" alt="X" className="x-logo-small-button" />
          Share on 𝕏
        </button>
        <button
          className={`play-again ${levelClass}`}
          onClick={() => {
            // Map level_id to URL path
            const levelId = session.level_id;

            // Mapping from level IDs to URL paths
            const levelRoutes = {
              'basic': '/mfersbeheaded/level/1',
              'house_songs': '/mfersbeheaded/level/2',
              'kids_friendly': '/mfersbeheaded/level/4',
              'nakamigo_crush': '/mfersbeheaded/pixelcrush',
              'pixelcrush': '/mfersbeheaded/pixelcrush',
              'tinydinos': '/mfersbeheaded/tinydinos',
            };

            // Get the route or fall back to level 1
            const route = levelRoutes[levelId] || '/mfersbeheaded/level/1';
            navigate(route);
          }}
        >
          Play this Level
        </button>
        <button className={`back-to-home ${levelClass}`} onClick={() => navigate('/mfersbeheaded/leaderboard')}>
          ← Leaderboard
        </button>
        {isLocalDev && (
          <>
            <button className={`play-again ${levelClass}`} onClick={() => setShowDebug(!showDebug)}>
              {showDebug ? '🐛 Hide Debug' : '🐛 Show Debug'}
            </button>
            <button className={`play-again ${levelClass}`} onClick={handleExportSession}>
              📥 Export JSON
            </button>
          </>
        )}
      </div>

      {/* Debug Panel - Only shown in local development */}
      {isLocalDev && showDebug && (
        <div className="debug-panel">
          <h3>Debug Information</h3>

          <div className="debug-section">
            <h4>Current State</h4>
            <pre>{JSON.stringify({
              currentEventIndex,
              currentTime: `${(currentTime / 1000).toFixed(2)}s`,
              score,
              moves,
              powerUps,
              isPlaying,
              highlightedSwap,
              totalEvents: events.length
            }, null, 2)}</pre>
          </div>

          <div className="debug-section">
            <h4>Session Info</h4>
            <pre>{JSON.stringify({
              session_id: session?.session_id,
              level_id: session?.level_id,
              level_name: session?.level_name,
              player_name: session?.player_name,
              twitter_user: session?.twitter_user?.screen_name,
              created_at: session?.created_at,
              final_score: session?.final_score,
              final_moves: session?.final_moves
            }, null, 2)}</pre>
          </div>

          <div className="debug-section">
            <h4>All Events ({events.length})</h4>
            <div className="debug-events-list">
              {events.map((event, idx) => (
                <div
                  key={idx}
                  className={`debug-event ${idx === currentEventIndex ? 'debug-event-active' : ''}`}
                  onClick={() => jumpToEvent(idx)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="debug-event-header">
                    <span className="debug-event-index">#{idx}</span>
                    <span className="debug-event-type" style={{ color: getEventColor(event.event_type) }}>
                      {event.event_type}
                    </span>
                    <span className="debug-event-time">
                      {(event.timestamp_ms / 1000).toFixed(2)}s
                    </span>
                  </div>
                  <details className="debug-event-details">
                    <summary>View Data</summary>
                    <pre>{JSON.stringify(event.data, null, 2)}</pre>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      </div>
    </div>

    {/* Background Music */}
    {session?.config?.BG_AUDIOS && (
      <BackgroundMusic
        audioRef={audioRef}
        gameState={1} // IN_PROGRESS state to play background music during replay
        musicMuted={musicMuted}
        audios={session.config.BG_AUDIOS}
        endAudios={session.config.END_AUDIOS || []}
      />
    )}
    </>
  );
}

export default ReplayViewer;
